/**
 * Mock API Utilities
 * 
 * Centralized utilities for creating consistent mock API responses
 */

export interface MockConfig {
  delay?: number
  shouldFail?: boolean
  errorMessage?: string
  errorCode?: string
}

export interface MockResponse<T> {
  success: boolean
  data?: T
  message?: string
  error?: string
  status: number
}

export const mockDelay = (ms: number = 1000): Promise<void> => {
  return new Promise(resolve => setTimeout(resolve, ms))
}

export const createMockSuccessResponse = <T>(
  data: T,
  message?: string
): MockResponse<T> => ({
  success: true,
  data,
  message: message || 'Success',
  status: 200
})

export const createMockErrorResponse = (
  error: string,
  code: string = 'MOCK_ERROR',
  status: number = 400
): MockResponse<never> => ({
  success: false,
  error,
  message: error,
  status
})

export const createMockApi = <T>(
  data: T,
  config: MockConfig = {}
): (() => Promise<MockResponse<T>>) => {
  return async () => {
    const { delay = 1000, shouldFail = false, errorMessage = 'Mock error', errorCode = 'MOCK_ERROR' } = config
    
    await mockDelay(delay)
    
    if (shouldFail) {
      throw new Error(errorMessage)
    }
    
    return createMockSuccessResponse(data)
  }
}

export const createMockApiWithError = <T>(
  errorMessage: string,
  config: MockConfig = {}
): (() => Promise<MockResponse<T>>) => {
  return async () => {
    const { delay = 1000, errorCode = 'MOCK_ERROR' } = config
    
    await mockDelay(delay)
    
    return createMockErrorResponse(errorMessage, errorCode)
  }
}

// Common mock error messages
export const MOCK_ERRORS = {
  UNAUTHORIZED: 'Unauthorized access',
  NOT_FOUND: 'Resource not found',
  VALIDATION_ERROR: 'Validation failed',
  NETWORK_ERROR: 'Network connection failed',
  SERVER_ERROR: 'Internal server error',
  RATE_LIMIT: 'Rate limit exceeded'
} as const 